import type { KeyboardEvent, ReactNode } from 'react'
import {
  cloneElement,
  createElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { shouldIgnoreKeyboardEvent } from '@/utils/keyboard-utils'
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react'
import { ColumnDef, Row, SortingState } from '@tanstack/react-table'

import { ColumnMeta, DataTableColumn, UseTableColumnsReturn } from '../types'

interface ColumnItem {
  id: string
  label: string
  description?: string
  isVisible: boolean
  isFixed?: boolean
  isSystem?: boolean
  isCustomField?: boolean
  order?: number
}

function SpacerHeader() {
  // Spacer header is now empty - the Add Column button is rendered as a floating element
  // in the DataTable component itself for better visibility when scrolling
  return createElement('div', { className: 'w-full h-full' })
}

/**
 * Source cell functions, keyed by each generated columnDef's meta object
 * (both are recreated together on every rebuild, so lookups are always
 * fresh). A WeakMap side-channel — rather than a meta field — keeps the
 * exported ColumnMeta interface untouched.
 */
const stableCellRenderers = new WeakMap<object, (item: unknown) => ReactNode>()

/**
 * Stable cell renderer shared by every generated column.
 *
 * flexRender treats a function `cell` as a component TYPE. If we assigned a
 * fresh closure per column rebuild (which happens on any upstream identity
 * change — e.g. a task cache write regenerating the column factory), React
 * would see a new component type and unmount/remount the whole cell subtree,
 * killing any open editor popover mid-interaction. Keeping ONE module-level
 * renderer keeps the type stable across rebuilds; the current cell function
 * travels through the WeakMap above as ordinary data instead.
 */
function StableColumnCell(props: unknown): ReactNode {
  const { row, column } = props as {
    row: Row<unknown>
    column: { columnDef: { meta?: object } }
  }
  const meta = column.columnDef.meta
  const renderCell = meta ? stableCellRenderers.get(meta) : undefined
  if (!renderCell) return null
  try {
    // If row.original is missing, fall back to rendering with the raw row so
    // the table doesn't crash on malformed data.
    if (!row?.original) {
      return renderCell(row)
    }
    return renderCell(row.original)
  } catch {
    return createElement('span', {}, 'Error')
  }
}

function StableSpacerCell() {
  return createElement('div', { className: 'w-full h-full' }, '')
}

interface UseTableColumnsProps<T> {
  columns: DataTableColumn<T>[]
  enableSorting: boolean
  enableFiltering: boolean
  enableColumnResizing: boolean
  enableCustomizeColumn?: boolean
  onSortingChange?: (sorting: SortingState) => void
  hiddenColumns?: Array<{ id: string; label: string; description?: string }>
  onEnableColumn?: (columnId: string) => void
  // Field type selection functionality
  fieldTypeOptions?: Array<{
    value: string
    label: string
    description: string
  }>
  onFieldTypeSelect?: (fieldType: string) => void
  // Current sorting state from parent (for syncing with external state)
  currentSorting?: SortingState
  customizePopoverKey?: string
  // Table customizer props
  allColumns?: ColumnItem[]
  onColumnVisibilityChange?: (
    columnId: string,
    isVisible: boolean,
  ) => Promise<void>
  onColumnsReorder?: (reorderedColumns: ColumnItem[]) => Promise<void>
  showCustomizeButton?: boolean
  canModifyColumns?: boolean
}

export function useTableColumns<T>({
  columns,
  enableSorting,
  enableFiltering,
  enableColumnResizing,
  enableCustomizeColumn = false,
  onSortingChange,
  hiddenColumns,
  onEnableColumn,
  fieldTypeOptions,
  onFieldTypeSelect,
  currentSorting,
  customizePopoverKey,
  // Table customizer props
  allColumns,
  onColumnVisibilityChange,
  onColumnsReorder,
  showCustomizeButton = false,
  canModifyColumns = true,
}: UseTableColumnsProps<T>): UseTableColumnsReturn<T> {
  // Track current column sorting locally
  const [currentSortColumn, setCurrentSortColumn] = useState<string | null>(
    null,
  )
  const [isDescending, setIsDescending] = useState(false)
  const [sorting, setSorting] = useState<SortingState>([])

  // Sync with external sorting state when it changes
  useEffect(() => {
    if (currentSorting && currentSorting.length > 0) {
      const sortItem = currentSorting[0]
      if (sortItem) {
        const nextIsDescending = !!sortItem.desc
        if (currentSortColumn !== sortItem.id) {
          setCurrentSortColumn(sortItem.id)
        }
        if (isDescending !== nextIsDescending) {
          setIsDescending(nextIsDescending)
        }
        if (
          sorting.length !== currentSorting.length ||
          sorting[0]?.id !== sortItem.id ||
          sorting[0]?.desc !== sortItem.desc
        ) {
          setSorting(currentSorting)
        }
      }
    } else if (currentSorting && currentSorting.length === 0) {
      // Clear sorting if external state is cleared
      if (currentSortColumn !== null) {
        setCurrentSortColumn(null)
      }
      if (isDescending) {
        setIsDescending(false)
      }
      if (sorting.length > 0) {
        setSorting([])
      }
    }
  }, [currentSorting, currentSortColumn, isDescending, sorting])

  // Sync local sorting state with internal changes (from user clicks)
  useEffect(() => {
    if (sorting.length > 0) {
      const sortItem = sorting[0]
      if (sortItem) {
        const nextIsDescending = !!sortItem.desc
        if (currentSortColumn !== sortItem.id) {
          setCurrentSortColumn(sortItem.id)
        }
        if (isDescending !== nextIsDescending) {
          setIsDescending(nextIsDescending)
        }
      }
    }
  }, [currentSortColumn, isDescending, sorting])

  // Create memoized column definitions
  const memoizedColumns = useMemo((): ColumnDef<T>[] => {
    return columns.map(column => {
      const meta = {
        className: column.className,
        width: column.width,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
        enableResizing:
          column.id === 'view'
            ? false
            : column.enableResizing !== false && enableColumnResizing,
        label:
          column.label ||
          (typeof column.header === 'string' ? column.header : undefined),
        description: column.description,
        fieldType: column.fieldType,
        customFieldDefinitionId: column.customFieldDefinitionId,
        dropdownOptions: column.dropdownOptions,
        userOptions: column.userOptions,
        hideColumnMenu: column.hideColumnMenu,
        clientPortalVisibility: column.clientPortalVisibility,
      } as ColumnMeta
      stableCellRenderers.set(meta, column.cell as (item: unknown) => ReactNode)

      return {
      id: column.id,
      // sortNullsLast columns sink empty values to the end in both directions.
      // TanStack's sortUndefined only acts on `undefined`, so coerce null→undefined
      // here (the raw accessor would otherwise return null, which sorts as 0).
      ...(column.sortNullsLast ? { sortUndefined: 'last' as const } : {}),
      accessorFn:
        column.accessorFn ||
        ((row: T) => {
          const key = column.id as keyof T
          const value = row[key]
          if (column.sortNullsLast && value == null) return undefined
          return value
        }),
      header: () => {
        // Special handling for view column - no sorting, just display the header
        if (column.id === 'view') {
          return column.header
        }

        // Only show sort UI if column supports sorting
        const canSort = column.enableSorting !== false && enableSorting

        if (!canSort) {
          return column.header
        }

        // Determine sort direction from our local state
        const isActive = currentSortColumn === column.id
        const sortDirection = isActive ? (isDescending ? 'desc' : 'asc') : false

        // If sorting is enabled, create a clickable header with optimized event handling
        const handleClick = () => {
          if (currentSortColumn === column.id) {
            if (!isDescending) {
              // Currently ascending → switch to descending
              setIsDescending(true)
              const newSort = [{ id: column.id, desc: true }]
              setSorting(newSort)
              if (onSortingChange) onSortingChange(newSort)
            } else {
              // Currently descending → clear sort
              setCurrentSortColumn(null)
              setIsDescending(false)
              setSorting([])
              if (onSortingChange) onSortingChange([])
            }
          } else {
            // New column sort, default to ascending
            setCurrentSortColumn(column.id)
            setIsDescending(false)
            const newSort = [{ id: column.id, desc: false }]
            setSorting(newSort)
            if (onSortingChange) onSortingChange(newSort)
          }
        }

        const handleKeyDown = (e: KeyboardEvent) => {
          // Don't handle keyboard events if we're on an input field
          if (shouldIgnoreKeyboardEvent(e)) {
            return
          }

          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
          }
        }

        // Support both string and JSX headers (e.g., name column with row selection checkbox)
        const headerLabel =
          typeof column.header === 'string'
            ? column.header
            : column.label || column.id
        const headerContent =
          typeof column.header === 'string'
            ? createElement('span', { key: 'label' }, column.header)
            : isValidElement(column.header)
              ? cloneElement(column.header, {
                  key: column.header.key ?? 'label',
                })
              : createElement('span', { key: 'label' }, headerLabel)

        const sortIndicator = createElement(
          'span',
          {
            key: 'indicator',
            className: 'sort-indicator ml-1 flex items-center',
          },
          [
            sortDirection === 'asc'
              ? createElement(CaretUpIcon, {
                  key: 'up',
                  className: 'h-3.5 w-3.5 text-primary font-bold',
                  'aria-hidden': 'true',
                })
              : sortDirection === 'desc'
                ? createElement(CaretDownIcon, {
                    key: 'down',
                    className: 'h-3.5 w-3.5 text-primary font-bold',
                    'aria-hidden': 'true',
                  })
                : createElement(
                    'div',
                    {
                      key: 'neutral',
                      className:
                        'h-3.5 w-3.5 flex flex-col opacity-30 hover:opacity-70 transition-opacity duration-100',
                    },
                    [
                      createElement(CaretUpIcon, {
                        key: 'up-small',
                        className: 'h-2 w-2',
                        'aria-hidden': 'true',
                      }),
                      createElement(CaretDownIcon, {
                        key: 'down-small',
                        className: 'h-2 w-2 -mt-0.5',
                        'aria-hidden': 'true',
                      }),
                    ],
                  ),
          ],
        )

        return createElement(
          'div',
          {
            onClick: handleClick,
            className:
              'cursor-pointer select-none flex items-center gap-1 w-full',
            'data-sort-direction': sortDirection || 'none',
            title: `Sort by ${headerLabel}`,
            role: 'button',
            tabIndex: 0,
            'aria-label': `Sort by ${headerLabel} ${sortDirection === 'asc' ? 'descending' : 'ascending'}`,
            'aria-sort':
              sortDirection === 'asc'
                ? 'ascending'
                : sortDirection === 'desc'
                  ? 'descending'
                  : 'none',
            onKeyDown: handleKeyDown,
          },
          [headerContent, sortIndicator],
        )
      },
      cell: StableColumnCell,
      enableSorting: column.enableSorting ?? enableSorting,
      enableColumnFilter: column.enableFiltering ?? enableFiltering,
      meta,
      size: column.id === 'view' ? 45 : undefined,
      }
    })
  }, [
    columns,
    enableSorting,
    enableFiltering,
    enableColumnResizing,
    currentSortColumn,
    isDescending,
    onSortingChange,
  ])

  // Add customize column if enabled
  const columnsWithCustomize = useMemo((): ColumnDef<T>[] => {
    const baseColumns = memoizedColumns

    // Don't add separate customize column - we'll merge it into the spacer column

    // Always add spacer column at the end to fill remaining width
    // If customize is enabled, include the customize functionality in the spacer column
    const spacerColumn: ColumnDef<T> = {
      id: 'spacer',
      accessorFn: () => null,
      header: SpacerHeader,
      cell: StableSpacerCell,
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        className: 'spacer-column',
        width: '36px',
        minWidth: '36px',
        maxWidth: '36px',
        enableResizing: false,
        enableCustomizeColumn,
        fieldTypeOptions,
        onFieldTypeSelect,
        hiddenColumns,
        onEnableColumn,
        customizePopoverKey,
        // Table customizer props
        allColumns,
        onColumnVisibilityChange,
        onColumnsReorder,
        showCustomizeButton,
        canModifyColumns,
      } as ColumnMeta,
      size: 36,
    }

    return [...baseColumns, spacerColumn]
  }, [
    memoizedColumns,
    enableCustomizeColumn,
    fieldTypeOptions,
    onFieldTypeSelect,
    hiddenColumns,
    onEnableColumn,
    customizePopoverKey,
    allColumns,
    onColumnVisibilityChange,
    onColumnsReorder,
    showCustomizeButton,
    canModifyColumns,
  ])

  return {
    memoizedColumns,
    columnsWithCustomize,
    currentSortColumn,
    isDescending,
  }
}
